import React, { useState, useEffect } from 'react';
import './JoinPage.css';

const JoinPage = () => {
  const [joinCode, setJoinCode] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userPlatform, setUserPlatform] = useState(null);

  useEffect(() => {
    // Detect user's platform
    const detectPlatform = () => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isMac = /Mac/.test(userAgent) && !/iPhone|iPad|iPod/.test(userAgent);
      const isWindows = /Windows/.test(userAgent);
      
      if (isIOS) return 'ios';
      if (isAndroid) return 'android';
      if (isMac) return 'mac';
      if (isWindows) return 'windows';
      return 'other';
    };
    
    setUserPlatform(detectPlatform());
    
    // Extract join code from URL path
    const pathParts = window.location.pathname.split('/');
    const codeFromPath = pathParts[pathParts.length - 1];
    
    if (codeFromPath && codeFromPath !== 'join') {
      setJoinCode(codeFromPath);
      // Store join code for app handoff
      localStorage.setItem('pendingJoinCode', codeFromPath);
      console.log('Stored join code from URL:', codeFromPath);
      
      // Fetch group info (optional - you can add this later if you have an API)
      fetchGroupInfo(codeFromPath);
    } else {
      setError('Invalid join link');
    }
  }, []);

  const fetchGroupInfo = async (code) => {
    // Enhanced group info with better messaging
    setGroupInfo({
      name: 'NextLink Group',
      description: 'Connect instantly with your community',
      category: 'Community'
    });
  };

  const attemptToOpenApp = () => {
    setLoading(true);
    
    const deepLinkUrl = joinCode ? `myapp://join/${joinCode}` : 'myapp://';
    
    // Create a hidden iframe to attempt opening the app
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = deepLinkUrl;
    document.body.appendChild(iframe);
    
    // Also try direct navigation
    window.location.href = deepLinkUrl;
    
    // Set timeout to show app store if app doesn't open
    setTimeout(() => {
      setLoading(false);
      showAppStorePrompt();
    }, 2500);
    
    // Clean up iframe
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 5000);
  };

  const showAppStorePrompt = () => {
    const primaryStore = getPrimaryStoreButton();
    
    const userConfirmed = window.confirm(
      `NextLink app is not installed. Would you like to download it for your ${userPlatform === 'ios' ? 'iOS' : userPlatform === 'android' ? 'Android' : ''} device?`
    );
    
    if (userConfirmed) {
      window.open(primaryStore.href, '_blank');
    }
  };

  const handleDirectStoreRedirect = () => {
    let storeUrl;
    switch (userPlatform) {
      case 'ios':
        storeUrl = 'https://apps.apple.com/us/app/the-nextlink/id6743041580';
        break;
      case 'android':
        storeUrl = 'https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink';
        break;
      case 'mac':
        storeUrl = 'https://apps.apple.com/us/app/the-nextlink/id6743041580';
        break;
      default:
        storeUrl = 'https://nextlinkchat.com';
    }
    
    window.open(storeUrl, '_blank');
  };
  
  const getPrimaryStoreButton = () => {
    switch (userPlatform) {
      case 'ios':
      case 'mac':
        return {
          href: 'https://apps.apple.com/us/app/the-nextlink/id6743041580',
          image: '/app-store-badge.png',
          alt: 'Download on the App Store',
          className: 'store-badge'
        };
      case 'android':
        return {
          href: 'https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink',
          image: '/google-play-badge.png',
          alt: 'Get it on Google Play',
          className: 'store-badge'
        };
      default:
        return {
          href: 'https://apps.apple.com/us/app/the-nextlink/id6743041580',
          image: '/app-store-badge.png',
          alt: 'Download on the App Store',
          className: 'store-badge'
        };
    }
  };
  
  const getSecondaryStoreButton = () => {
    if (userPlatform === 'ios' || userPlatform === 'mac') {
      return {
        href: 'https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink',
        image: '/google-play-badge.png',
        alt: 'Get it on Google Play',
        className: 'store-badge'
      };
    } else if (userPlatform === 'android') {
      return {
        href: 'https://apps.apple.com/us/app/the-nextlink/id6743041580',
        image: '/app-store-badge.png',
        alt: 'Download on the App Store',
        className: 'store-badge'
      };
    }
    return null;
  };

  if (error) {
    return (
      <div className="landing-container">
        <div className="content-wrapper">
          <div className="hero-section">
            <h1>Invalid Join Link</h1>
            <p>This join link is not valid or may have expired.</p>
            <p className="subtitle">Don't worry, you can still explore NextLink!</p>
            <div className="cta-section">
              <button 
                onClick={() => window.location.href = 'https://nextlinkchat.com'}
                className="cta-button primary"
              >
                <span>🌐</span> Explore NextLink
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-container">
      <div className="content-wrapper">
        <div className="hero-section">
          <h1>Join {groupInfo?.name || 'NextLink Group'}</h1>
          {groupInfo?.description && (
            <p className="group-description">{groupInfo.description}</p>
          )}
          {groupInfo?.memberCount && (
            <p className="subtitle">{groupInfo.memberCount} • {groupInfo.category || 'Community'}</p>
          )}
          <p>You've been invited to join a group on The NextLink!</p>
          
        </div>

        <div className="cta-section">
          {loading ? (
            <div className="loading-state">
              <p>Opening NextLink app...</p>
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <>
              <button 
                onClick={attemptToOpenApp}
                className="cta-button primary"
              >
                <span>🚀</span> Open in NextLink App
              </button>
              
              <div className="download-options">
                <p>Don't have the app yet?</p>
                <button 
                  onClick={handleDirectStoreRedirect}
                  className="cta-button secondary"
                >
                  <span>📱</span> Download NextLink
                </button>
              </div>
              
              <div className="platform-badges">
                {(() => {
                  const primaryStore = getPrimaryStoreButton();
                  const secondaryStore = getSecondaryStoreButton();
                  
                  return (
                    <>
                      <a 
                        href={primaryStore.href}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={primaryStore.className}
                      >
                        <img 
                          src={primaryStore.image} 
                          alt={primaryStore.alt}
                        />
                      </a>
                      {secondaryStore && (
                        <a 
                          href={secondaryStore.href}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={secondaryStore.className}
                          style={{ opacity: 0.8 }}
                        >
                          <img 
                            src={secondaryStore.image} 
                            alt={secondaryStore.alt}
                          />
                        </a>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>

        <div className="instructions">
          <h3>How it works</h3>
          <ol>
            <li>Download the NextLink app from your device's app store</li>
            <li>Create your account and set up your profile</li>
            <li>Return to this link and you'll automatically join the group</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default JoinPage;